import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserIcon, LogOutIcon, XIcon } from 'lucide-react';
import { navGroups } from '../data/navigation';
import { View, parentView } from '../types/view';
interface SidebarProps {
  currentView: View;
  onNavigate: (view: View) => void;
  isAdmin: boolean;
  username: string;
  userRole: string;
  onLogout: () => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function Sidebar({
  currentView,
  onNavigate,
  isAdmin,
  username,
  userRole,
  onLogout,
  isMobileOpen,
  onMobileClose
}: SidebarProps) {
  const content = <div className="flex h-full flex-col bg-white" data-id="element-858" data-mp="3ld49v7f6wtdx">
      {/* Brand */}
      <div className="flex items-center justify-between gap-3 px-5 py-5" data-id="element-859" data-mp="3gesygs98yatl">
        <div className="flex items-center gap-2.5" data-id="element-860" data-mp="3cco7y2e3hub6">
          <span className="text-xl font-bold tracking-tight text-stone-900" data-id="element-861" data-mp="3cbd27x13achf">
            Lykke-EL
          </span>
          <span className="rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-semibold text-stone-500" data-id="element-862" data-mp="37ffntw9dzg7a">
            {userRole}
          </span>
        </div>
        <button onClick={onMobileClose} className="-mr-1 rounded-lg p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700 lg:hidden" aria-label="Luk menu" data-id="element-863" data-mp="1qr5kbun13rnv">
          <XIcon className="h-5 w-5" data-id="element-864" data-mp="1h0u4patzfu15" />
        </button>
      </div>

      {/* Navigation */}
      <nav aria-label="Hovednavigation" className="flex-1 overflow-y-auto px-3 pb-4" data-id="element-865" data-mp="24qy8bi767ts5">
        {navGroups.map(group => {
        const items = group.items.filter(item => !item.adminOnly || isAdmin);
        if (items.length === 0) return null;
        return <div key={group.label ?? 'main'} className="mb-1" data-id="element-866" data-mp="20n08t6dxogyk">
              {group.label && <h2 className="px-3 pb-1 pt-5 text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-867" data-mp="k8qm0gpcb09u">
                  {group.label}
                </h2>}
              <ul className="space-y-0.5" data-id="element-868" data-mp="2qxiv7ivf5mjy">
                {items.map(item => {
              const Icon = item.icon;
              const isActive = currentView === item.id || parentView[currentView] === item.id;
              return <li key={item.id} data-id="element-869" data-mp="2d64a96hybba9">
                      <button onClick={() => onNavigate(item.id)} aria-current={isActive ? 'page' : undefined} className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${isActive ? 'bg-brand-50 text-brand-700' : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'}`} data-id="element-870" data-mp="2d52vos1q86ns">
                        <Icon className={`h-[18px] w-[18px] flex-shrink-0 ${isActive ? 'text-brand-600' : 'text-stone-400'}`} data-id="element-871" data-mp="3omlwkdru3xj6" />
                        <span className="flex-1 truncate" data-id="element-872" data-mp="1fcd11gpgb22c">{item.label}</span>
                        {item.badge && <span className="flex-shrink-0 rounded bg-brand-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-brand-700" data-id="element-873" data-mp="1k565e3h0c8dw">
                            {item.badge}
                          </span>}
                      </button>
                    </li>;
            })}
              </ul>
            </div>;
      })}
      </nav>

      {/* Account */}
      <div className="border-t border-stone-200 p-3" data-id="element-874" data-mp="10iqc2mju5ibu">
        <button onClick={() => onNavigate('profile')} aria-current={currentView === 'profile' ? 'page' : undefined} className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${currentView === 'profile' ? 'bg-brand-50' : 'hover:bg-stone-100'}`} data-id="element-875" data-mp="15gjj1hsyqgd6">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-stone-900 text-xs font-semibold uppercase text-white" data-id="element-876" data-mp="2ffb3gkyoam5z">
            {username.slice(0, 2)}
          </span>
          <span className="min-w-0 flex-1" data-id="element-877" data-mp="2obtlv39a2rob">
            <span className="block truncate text-sm font-medium text-stone-900" data-id="element-878" data-mp="2kbhyhipj6d95">
              Profil
            </span>
            <span className="block truncate text-xs text-stone-500" data-id="element-879" data-mp="2yarg8mtdxff8">
              {username}
            </span>
          </span>
          <UserIcon className="h-4 w-4 flex-shrink-0 text-stone-400" data-id="element-880" data-mp="2tberfwiydya0" />
        </button>
        <button onClick={onLogout} className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-stone-500 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-881" data-mp="38487pk61uifq">
          <LogOutIcon className="h-[18px] w-[18px] flex-shrink-0" data-id="element-882" data-mp="3c40i82f06ynq" />
          Log ud
        </button>
      </div>
    </div>;
  return <>
      {/* Desktop */}
      <aside className="hidden w-64 flex-shrink-0 border-r border-stone-200 lg:block" data-id="element-883" data-mp="3vng1b1k9qpka">
        <div className="sticky top-0 h-screen" data-id="element-884" data-mp="4ig578kfx2ln">{content}</div>
      </aside>

      {/* Mobile slide-over */}
      <AnimatePresence data-id="element-885" data-mp="3mqx6fjsh8rl2">
        {isMobileOpen && <div className="lg:hidden" data-id="element-886" data-mp="3qpefi8vgkc54">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: EASE
        }} onClick={onMobileClose} className="fixed inset-0 z-40 bg-stone-900/40" data-id="element-887" data-mp="3qsirhsf60c6c" />
            <motion.div initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          duration: 0.26,
          ease: EASE
        }} className="fixed inset-y-0 left-0 z-50 w-[280px] max-w-[85vw] overflow-hidden border-r border-stone-200 pb-16 shadow-xl" data-id="element-888" data-mp="32z0l4bpei17d">
              {content}
            </motion.div>
          </div>}
      </AnimatePresence>
    </>;
}